import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {ReactNode, useMemo} from 'react';

import {assert} from 'Asserts';
import {
  BenchlingExpressionFunction,
  BenchlingExpression,
  BenchlingExpressionType,
  BenchlingExpressionConstantValue,
  BenchlingExpressionSchemaLookup,
  BenchlingExpressionContextVariable,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  getDisplayNameFromFunctionSignature,
  BenchlingFunctionSignature,
} from 'benchling-expressions/BenchlingFunctionSignatures';
import {
  LeafType,
  isBenchlingTypeLeafType,
  isLeafTypePrimitive,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LookupConfigDescription} from 'lookup-config/LookupConfigDescription';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {expressionFunctionIsBoolean} from 'processes/modals/router-config/RouterConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaFieldSelectorOption} from 'schemas/SchemaFieldSelectorConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {assertNever} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

export enum SpecialExpressionType {
  DEFAULT_EXPRESSION = 'DEFAULT_EXPRESSION',
  NO_BLOCKING_ROUTERS = 'NO_BLOCKING_ROUTERS',
  INACCESSIBLE = 'INACCESSIBLE',
}

const SPECIAL_EXPRESSION_TYPE_TO_TEXT: Record<SpecialExpressionType, string> = {
  [SpecialExpressionType.DEFAULT_EXPRESSION]: 'Otherwise',
  [SpecialExpressionType.NO_BLOCKING_ROUTERS]: 'Always',
  [SpecialExpressionType.INACCESSIBLE]: 'No access',
};

type TaskOrOutputSchema =
  | {__typename: 'ProcessTaskSchemaNode'; id: string; name: string}
  | {__typename: 'ProcessOutputSchemaNode'; id: string; name: string}
  | InaccessibleInfoGraphQL;

export interface SchemaMapper {
  __typename: 'ProcessFlowchartSchemaMapperNode';
  id: string;
  toNodeConfig: {__typename: 'ProcessFlowchartNodeConfigNode'; id: string} | null;
  fromSchemas: Array<TaskOrOutputSchema>;
  toSchema: TaskOrOutputSchema;
}

export interface MapperAndBlockingExpression {
  isSchemaMapper: true;
  schemaMapper: SchemaMapper;
  expression: BenchlingExpressionFunction | SpecialExpressionType;
}

export interface EndNodeAndBlockingExpression {
  isSchemaMapper: false;
  endNode: {id: string; name: string};
  expression: BenchlingExpressionFunction | SpecialExpressionType;
  fromSchemas: Array<TaskOrOutputSchema>;
}

export type MapperOrEndNodeAndBlockingExpression = MapperAndBlockingExpression | EndNodeAndBlockingExpression;

export interface RoutingRoutesDisplayProps {
  mapperOrEndNodeAndBlockingExpressions: Array<MapperOrEndNodeAndBlockingExpression>;
  mapperIdsToDownstreamSchemaName: {[id: string]: string};
  allowedRoutingFunctionsByName: {[name: string]: BenchlingFunctionSignature};
  selectorsById: SchemaFieldSelectorsDict;
  allSchemasById: SchemaById;
  flowchartTaskSchema: Schema | null;
}

interface ExpressionComparisonDisplayProps {
  expression: BenchlingExpressionFunction;
  selectorOptionsById: {[id: string]: SchemaFieldSelectorOption};
  baseSchemaForLookup: Schema | null;
  allowedRoutingFunctionsByName: {[name: string]: BenchlingFunctionSignature};
  flowchartTaskSchema: Schema;
}

interface NonFunctionExpressionDisplayProps {
  baseSchemaForLookup: Schema | null;
  expression:
    | BenchlingExpressionConstantValue
    | BenchlingExpressionSchemaLookup
    | BenchlingExpressionContextVariable;
  selectorOptionsById: {[id: string]: SchemaFieldSelectorOption};
  flowchartTaskSchema: Schema;
}

interface ExpressionDisplayProps {
  mapperOrEndNodeAndBlockingExpression: MapperAndBlockingExpression | EndNodeAndBlockingExpression;
  idx: number;
  allSchemasById: SchemaById;
  mapperIdsToDownstreamSchemaName: {[id: string]: string};
  allowedRoutingFunctionsByName: {[name: string]: BenchlingFunctionSignature};
  selectorOptionsById: {[id: string]: SchemaFieldSelectorOption};
  flowchartTaskSchema: Schema | null;
}

function isBenchlingExpression(
  expression: BenchlingExpression | SpecialExpressionType
): expression is BenchlingExpression {
  return (expression as BenchlingExpression).type != null;
}

function NonFunctionExpressionDisplay(props: NonFunctionExpressionDisplayProps): JSX.Element | null {
  switch (props.expression.type) {
    case BenchlingExpressionType.SCHEMA_LOOKUP:
      return (
        <LookupConfigDescription
          baseSchema={props.baseSchemaForLookup}
          lookupConfig={props.expression.lookupConfig}
          context={{flowchartTaskSchema: props.flowchartTaskSchema}}
        />
      );
    case BenchlingExpressionType.CONTEXT_VARIABLE:
      return <>{props.expression.contextKey}</>;
    case BenchlingExpressionType.CONSTANT_VALUE: {
      const itemType = props.expression.itemType;
      if (itemType.name === ComplexTypeOperator.LIST) {
        assert(
          Array.isArray(props.expression.value),
          'Expected the value of a list type constant value to be an array'
        );
        return (
          <>
            {props.expression.value.map((v, idx) => (
              <React.Fragment key={idx}>
                <NonFunctionExpressionDisplay
                  baseSchemaForLookup={props.baseSchemaForLookup}
                  expression={{
                    type: BenchlingExpressionType.CONSTANT_VALUE,
                    value: v,
                    itemType: {name: itemType.ofType, selectorId: itemType.selectorId},
                  }}
                  selectorOptionsById={props.selectorOptionsById}
                  flowchartTaskSchema={props.flowchartTaskSchema}
                />
                &nbsp;
              </React.Fragment>
            ))}
          </>
        );
      } else if (isBenchlingTypeLeafType(itemType.name)) {
        if (isLeafTypePrimitive(itemType.name)) {
          return (
            <>
              {props.expression.itemType.name === LeafType.TEXT
                ? `'${props.expression.value}'`
                : (props.expression.value as ReactNode)}
            </>
          );
        } else if (itemType.name === LeafType.DROPDOWN_OPTION) {
          const dropdownValue = props.selectorOptionsById[props.expression.value as string]?.name;
          return dropdownValue != null ? <>'{dropdownValue}'</> : <>No access</>;
        }
      }
      assert(false, `Unexpected constant value type`, {expression: props.expression});
      return null;
    }
    default:
      assert(false, `Unexpected expression ${assertNever(props.expression)}`);
      return null;
  }
}

function ExpressionComparisonDisplay(props: ExpressionComparisonDisplayProps): JSX.Element {
  const isBooleanOperator = expressionFunctionIsBoolean(props.expression);

  if (isBooleanOperator) {
    return (
      <>
        (
        {props.expression.parameters.map((param, idx) => (
          <React.Fragment key={idx}>
            {idx !== 0 ? (
              <>
                <br />
                <span>{props.expression.name.toLowerCase()}&nbsp;</span>
              </>
            ) : null}
            <ExpressionComparisonDisplay
              {...props}
              expression={param.expression as BenchlingExpressionFunction}
            />
          </React.Fragment>
        ))}
        )
      </>
    );
  }

  const parameterOneExpression = props.expression.parameters[0].expression;
  const parameterTwoExpression = props.expression.parameters[1]?.expression;
  assert(
    parameterOneExpression.type !== BenchlingExpressionType.FUNCTION &&
      (parameterTwoExpression === undefined ||
        parameterTwoExpression.type !== BenchlingExpressionType.FUNCTION),
    'Functions cannot be nested',
    {
      expression: props.expression,
    }
  );

  return (
    <>
      <NonFunctionExpressionDisplay
        baseSchemaForLookup={props.baseSchemaForLookup}
        expression={parameterOneExpression}
        selectorOptionsById={props.selectorOptionsById}
        flowchartTaskSchema={props.flowchartTaskSchema}
      />
      &nbsp;
      {getDisplayNameFromFunctionSignature(props.allowedRoutingFunctionsByName[props.expression.name])}
      {parameterTwoExpression == null ? null : (
        <>
          &nbsp;
          <NonFunctionExpressionDisplay
            baseSchemaForLookup={props.baseSchemaForLookup}
            expression={parameterTwoExpression}
            selectorOptionsById={props.selectorOptionsById}
            flowchartTaskSchema={props.flowchartTaskSchema}
          />
        </>
      )}
    </>
  );
}

function isMapperAndBlockingExpression(
  mapperOrEndNodeAndBlockingExpression: MapperOrEndNodeAndBlockingExpression
): mapperOrEndNodeAndBlockingExpression is MapperAndBlockingExpression {
  return mapperOrEndNodeAndBlockingExpression.isSchemaMapper;
}

function ExpressionDisplay(props: ExpressionDisplayProps): JSX.Element {
  const destinationAndBlockingExpression = props.mapperOrEndNodeAndBlockingExpression;
  const isSchemaMapper = isMapperAndBlockingExpression(destinationAndBlockingExpression);
  const mapperOrEndNodeId = isSchemaMapper
    ? destinationAndBlockingExpression.schemaMapper.id
    : destinationAndBlockingExpression.endNode.id;
  let baseSchema;
  if (isSchemaMapper) {
    const schemaMapper = destinationAndBlockingExpression.schemaMapper;
    baseSchema =
      schemaMapper.fromSchemas[0] == null || isInaccessible(schemaMapper.fromSchemas[0])
        ? null
        : (props.allSchemasById[schemaMapper.fromSchemas[0].id] ?? null);
  } else {
    baseSchema =
      destinationAndBlockingExpression.fromSchemas[0] == null ||
      isInaccessible(destinationAndBlockingExpression.fromSchemas[0])
        ? null
        : (props.allSchemasById[destinationAndBlockingExpression.fromSchemas[0].id] ?? null);
  }

  const chipIcon = isSchemaMapper ? 'process' : 'do-not-enter';
  return (
    <div
      className={css(styles.routingRoute)}
      {...getTestProps({
        component: 'RoutingRoutesDisplay',
        element: 'comparison',
        key: `${mapperOrEndNodeId}`,
        index: `${props.idx}`,
      })}
      data-amp-safe={false}
    >
      {isBenchlingExpression(props.mapperOrEndNodeAndBlockingExpression.expression) ? (
        <>
          <span>If&nbsp;</span>
          <ExpressionComparisonDisplay
            baseSchemaForLookup={baseSchema}
            expression={props.mapperOrEndNodeAndBlockingExpression.expression}
            selectorOptionsById={props.selectorOptionsById}
            allowedRoutingFunctionsByName={props.allowedRoutingFunctionsByName}
            flowchartTaskSchema={props.flowchartTaskSchema!}
          />
          <span>, then go to&nbsp;</span>
        </>
      ) : (
        <span>
          {SPECIAL_EXPRESSION_TYPE_TO_TEXT[props.mapperOrEndNodeAndBlockingExpression.expression]} go to&nbsp;
        </span>
      )}
      <Chip
        size={ChipSize.TABLE_CELL}
        text={
          isSchemaMapper
            ? props.mapperIdsToDownstreamSchemaName[destinationAndBlockingExpression.schemaMapper.id]
            : destinationAndBlockingExpression.endNode.name
        }
        icon={chipIcon}
        className={css(styles.alignMiddle)}
      />
    </div>
  );
}

export default function RoutingRoutesDisplay(props: RoutingRoutesDisplayProps): JSX.Element {
  const selectorOptionsById = useMemo(
    () =>
      groupByUniqueKey(
        Object.values(props.selectorsById)
          .map((selector) => selector.allSchemaFieldSelectorOptions)
          .flat(),
        ({id}) => id
      ),
    [props.selectorsById]
  );

  const sortedMappersOrEndNodesAndBlockingExpressions = useMemo(
    () =>
      produce(props.mapperOrEndNodeAndBlockingExpressions, (draftMappersAndBlockingExpressions) => {
        draftMappersAndBlockingExpressions.sort((mapperAndExpressionA, mapperAndExpressionB) => {
          if (mapperAndExpressionA.expression === SpecialExpressionType.DEFAULT_EXPRESSION) {
            return 1;
          } else if (mapperAndExpressionA.expression === SpecialExpressionType.NO_BLOCKING_ROUTERS) {
            return -1;
          }
          if (mapperAndExpressionB.expression === SpecialExpressionType.DEFAULT_EXPRESSION) {
            return -1;
          } else if (mapperAndExpressionB.expression === SpecialExpressionType.NO_BLOCKING_ROUTERS) {
            return 1;
          }
          return (
            mapperAndExpressionA.isSchemaMapper
              ? props.mapperIdsToDownstreamSchemaName[mapperAndExpressionA.schemaMapper.id]
              : mapperAndExpressionA.endNode.id
          ).localeCompare(
            mapperAndExpressionB.isSchemaMapper
              ? props.mapperIdsToDownstreamSchemaName[mapperAndExpressionB.schemaMapper.id]
              : mapperAndExpressionB.endNode.id
          );
        });
      }),
    [props.mapperOrEndNodeAndBlockingExpressions, props.mapperIdsToDownstreamSchemaName]
  );

  return (
    <div>
      {sortedMappersOrEndNodesAndBlockingExpressions.map((mapperOrEndNodeAndBlockingExpression, idx) => (
        <ExpressionDisplay
          mapperOrEndNodeAndBlockingExpression={mapperOrEndNodeAndBlockingExpression}
          key={idx}
          idx={idx}
          allSchemasById={props.allSchemasById}
          mapperIdsToDownstreamSchemaName={props.mapperIdsToDownstreamSchemaName}
          allowedRoutingFunctionsByName={props.allowedRoutingFunctionsByName}
          selectorOptionsById={selectorOptionsById}
          flowchartTaskSchema={props.flowchartTaskSchema}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  routingRoute: {
    display: 'block',
    background: Colors.GRAY_LIGHTER,
    padding: 12,
    marginBottom: 8,
  },

  alignMiddle: {
    verticalAlign: 'middle',
  },
});
